import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  connectedCallback() {
    super.connectedCallback();

    this.loaded = false;
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (this.prefersReducedMotion) return;

    if (!('IntersectionObserver' in window)) {
      this.loadVideo();
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        this.loadVideo();
        this.observer?.disconnect();
      },
      { rootMargin: '400px 0px' }
    );

    this.observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.observer?.disconnect();
  }

  loadVideo() {
    if (this.loaded) return;
    this.loaded = true;

    const { videoSources, videoElement } = this.refs;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) {
        source.setAttribute('src', videoSource);
        source.removeAttribute('data-video-source');
      }
    }

    videoElement.preload = 'metadata';
    videoElement.load();
    videoElement.play().catch(() => {});
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
